import React, {
  createContext,
  type Dispatch,
  type SetStateAction,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
} from 'react'
import { DeletedStartingPersonDialog, getTreeStartingPid, SelectPersonOverlay } from '@fs/zion-tree-groups'
import { usePerson } from '@fs/zion-person-service'
import { type PID, type GroupType, type TreeId } from '@fs/zion-tree-types'
import { buildLangUrl } from '@fs/zion-router'
import { useOverlay, type UseOverlayReturnOptions } from '@fs/zion-ui'
import getTreeUrl from '../helpers/getTreeUrl'

type StartingPersonValue = {
  loading: boolean
  startingPersonId?: PID
  startingPersonDeleted: boolean
  setStartingPersonId: Dispatch<SetStateAction<PID | undefined>>
  refetchStartingPersonPid: () => void
  startingPersonOverlay: UseOverlayReturnOptions
  deletedStartingPersonOverlay: UseOverlayReturnOptions
}

const StartingPersonContext = createContext<StartingPersonValue | undefined>(undefined)

export const useStartingPerson = (): StartingPersonValue => {
  const context = useContext(StartingPersonContext)
  if (!context) {
    throw new Error('useStartingPerson must be used within a StartingPersonProvider')
  }
  return context
}

type StartingPersonProviderProps = {
  treeId?: TreeId
  types?: GroupType[]
  children: ReactNode
}

export default function StartingPersonProvider({ treeId, types, children }: StartingPersonProviderProps): JSX.Element {
  // A person the user actively picked (via the select-person overlay) always wins over the
  // stored pid. Undefined until the user selects one this session.
  const [selectedPid, setSelectedPid] = useState<PID>()
  // The raw starting pid stored on the tree, before we've verified the person still exists.
  const [storedPid, setStoredPid] = useState<PID>()
  const [loading, setLoading] = useState(true)
  const isTypeTree = types?.includes('PRIVATE_PERSON_SHARING') || types?.includes('RESEARCHER_TREE')
  const lastTreeStartingPersonDataWasFetched = useRef<TreeId | false>(false)
  const isCurrent = useRef<boolean>()
  const startingPersonOverlay = useOverlay()
  const deletedStartingPersonOverlay = useOverlay()
  // True while the select-person overlay was opened from the deleted-person "View Tree"
  // flow, so selecting a new person should continue on to the pedigree. The ordinary
  // "Change" button and the never-set auto-open leave this false and stay put.
  const viewTreeAfterSelect = useRef(false)

  // Verify the stored starting person still exists. A previously-selected starting person
  // can be deleted from the tree; when that happens we must not keep using the dead pid.
  // `undefined` means the lookup is still in flight.
  const storedPerson = usePerson(storedPid, 'summary', undefined, false, true)
  const verifyingStartingPerson = storedPid !== undefined && storedPerson === undefined
  const storedPersonDeleted =
    storedPid !== undefined &&
    storedPerson !== undefined &&
    (storedPerson.id === 'UNKNOWN' || storedPerson.deleted === true)

  // Derive the active starting person synchronously so consumers never observe a transient
  // "no starting person" state for a person that is still being verified (which would, e.g.,
  // wrongly trip the settings page's auto-open of the select-person overlay).
  const startingPersonId = selectedPid ?? (storedPersonDeleted || verifyingStartingPerson ? undefined : storedPid)
  const startingPersonDeleted = !selectedPid && storedPersonDeleted

  const onSelectStartingPerson = useCallback<NonNullable<ComponentProps<typeof SelectPersonOverlay>['onSelect']>>(
    (person) => {
      if (person.id !== 'UNKNOWN') {
        setSelectedPid(person.id)
        // Re-verify against the freshly chosen person so a prior "deleted" state clears.
        setStoredPid(person.id)

        // When the select flow was reached from the deleted-person "View Tree" dialog,
        // complete that original intent by continuing on to the pedigree, just as the
        // View Tree button would have.
        if (viewTreeAfterSelect.current) {
          const treeUrl = getTreeUrl({ treeId, pedigreeType: 'landscape', mePersonId: person.id })
          window.location.assign(buildLangUrl(treeUrl, true))
        }
      }
      viewTreeAfterSelect.current = false
      startingPersonOverlay.close()
    },
    [startingPersonOverlay, treeId]
  )

  const onContinueFromDeletedDialog = useCallback(() => {
    viewTreeAfterSelect.current = true
    deletedStartingPersonOverlay.close()
    startingPersonOverlay.open()
  }, [deletedStartingPersonOverlay, startingPersonOverlay])

  useEffect(() => {
    isCurrent.current = true
    return () => {
      isCurrent.current = false
    }
  })

  // If the select-person overlay closes without a selection (e.g. the user cancels the
  // deleted-person flow), drop the pending navigation so a later "Change" selection
  // doesn't unexpectedly redirect to the pedigree.
  useEffect(() => {
    if (!startingPersonOverlay.isOpen && !startingPersonOverlay.transitioning) {
      viewTreeAfterSelect.current = false
    }
  }, [startingPersonOverlay.isOpen, startingPersonOverlay.transitioning])

  const refetchStartingPersonPid = useCallback(() => {
    lastTreeStartingPersonDataWasFetched.current = false
  }, [])

  if (treeId && lastTreeStartingPersonDataWasFetched.current !== treeId) {
    lastTreeStartingPersonDataWasFetched.current = treeId
    const noCache = true

    getTreeStartingPid(treeId, noCache)
      .then(({ personId }) => {
        if (isCurrent.current && personId && isTypeTree) {
          // TODO update getTreeStartingPid ReturnType to narrow to PID and TreeId.
          setStoredPid(personId as PID)
        }

        return undefined
      })
      .catch(console.error)
      .finally(() => isCurrent.current && setLoading(false))
  }

  const value = useMemo(
    () =>
      ({
        loading: loading || verifyingStartingPerson,
        startingPersonId,
        startingPersonDeleted,
        setStartingPersonId: setSelectedPid,
        refetchStartingPersonPid,
        startingPersonOverlay,
        deletedStartingPersonOverlay,
      }) satisfies StartingPersonValue,
    [
      loading,
      verifyingStartingPerson,
      startingPersonId,
      startingPersonDeleted,
      refetchStartingPersonPid,
      startingPersonOverlay,
      deletedStartingPersonOverlay,
    ]
  )
  return (
    <>
      <StartingPersonContext.Provider value={value}>{children}</StartingPersonContext.Provider>
      {(startingPersonOverlay.isOpen || startingPersonOverlay.transitioning) && (
        <SelectPersonOverlay mode="startingPerson" onSelect={onSelectStartingPerson} overlay={startingPersonOverlay} />
      )}
      {(deletedStartingPersonOverlay.isOpen || deletedStartingPersonOverlay.transitioning) && (
        <DeletedStartingPersonDialog overlay={deletedStartingPersonOverlay} onContinue={onContinueFromDeletedDialog} />
      )}
    </>
  )
}
